import { DOCUMENT } from '@angular/common';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { Inject, Injectable, OnDestroy } from '@angular/core';
import { environment } from 'src/environments/environment';

@Injectable({ providedIn: 'root' })
export class AuthenticatedBrowserNavigationService implements OnDestroy {
  private installed = false;
  private readonly clickHandler = (event: MouseEvent) => this.onDocumentClick(event);
  private readonly messageHandler = (event: MessageEvent) => this.onReportNavigationMessage(event);

  constructor(
    private http: HttpClient,
    @Inject(DOCUMENT) private document: Document
  ) {}

  install(): void {
    if (this.installed || !this.document) {
      return;
    }

    this.document.addEventListener('click', this.clickHandler, true);
    window.addEventListener('message', this.messageHandler, false);
    this.installed = true;
  }

  ngOnDestroy(): void {
    if (!this.installed || !this.document) {
      return;
    }

    this.document.removeEventListener('click', this.clickHandler, true);
    window.removeEventListener('message', this.messageHandler, false);
    this.installed = false;
  }

  isAuthenticatedBackendRoute(href: string): boolean {
    const absolute = this.toBackendAbsoluteUrl(href);
    if (!absolute) {
      return false;
    }

    const relative = absolute.substr(environment.BaseUrl.length).replace(/^\/+/, '');
    if (/^file\//i.test(relative)) {
      return false;
    }

    return /^(fish\/(show|qrcode|print|print2|letterpreorder)|letterrequestcustomerprint\/|colleagueprint\/print|protocol17025print\/|flowchart\/workflow|flowchart\/workflowdetail|flowchart\/flowchartt|labcertificate|labconfirmcalibration|labcalibration|labexitreview|labdownloaduploaddevice|labdownloaddeviceform|labuploaddeviceform|daro\/kartable\/getcalibrationinlocationdownloaddevicebehfa|certificateno01\/show|devicebehfaview\/)/i.test(relative);
  }

  openAuthenticatedRoute(href: string, target: string = '_blank', existingWindow?: Window): void {
    const absolute = this.toBackendAbsoluteUrl(href);
    if (!absolute || !this.isAuthenticatedBackendRoute(absolute)) {
      return;
    }

    const reportWindow = existingWindow || window.open('', target || '_blank');
    if (!reportWindow) {
      return;
    }

    this.http.get(absolute, { observe: 'response', responseType: 'text' }).subscribe(
      (response: HttpResponse<string>) => {
        this.writeReportHtml(reportWindow, response.body || '', absolute);
      },
      () => {
        try {
          reportWindow.close();
        } catch {
        }
      }
    );
  }

  private onDocumentClick(event: MouseEvent): void {
    const anchor = this.findAnchor(event.target as HTMLElement);
    if (!anchor || !anchor.href || anchor.target !== '_blank') {
      return;
    }

    if (!this.isAuthenticatedBackendRoute(anchor.href)) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    this.openAuthenticatedRoute(anchor.href, anchor.target);
  }

  private onReportNavigationMessage(event: MessageEvent): void {
    if (!event || event.origin !== window.location.origin || !event.data || event.data.type !== 'daro-auth-report-nav') {
      return;
    }

    const targetWindow = event.source as Window;
    this.openAuthenticatedRoute(event.data.url, '_self', targetWindow);
  }

  private findAnchor(element: HTMLElement): HTMLAnchorElement | null {
    let current: HTMLElement | null = element;
    while (current && current !== this.document.body) {
      if (current.tagName && current.tagName.toLowerCase() === 'a') {
        return current as HTMLAnchorElement;
      }
      current = current.parentElement;
    }

    return null;
  }

  private toBackendAbsoluteUrl(href: string): string | null {
    if (!href) {
      return null;
    }

    try {
      const url = new URL(href, environment.BaseUrl);
      const tokenKeys: string[] = [];
      url.searchParams.forEach((_, key) => {
        if (key.toLowerCase() === 'token') {
          tokenKeys.push(key);
        }
      });
      tokenKeys.forEach(key => url.searchParams.delete(key));
      const absolute = url.toString();
      return absolute.indexOf(environment.BaseUrl) === 0 ? absolute : null;
    } catch {
      return null;
    }
  }

  private writeReportHtml(reportWindow: Window, html: string, requestedUrl: string): void {
    const htmlWithBase = this.injectReportBridgeScript(this.injectBaseHref(html), requestedUrl);
    reportWindow.document.open();
    reportWindow.document.write(htmlWithBase);
    reportWindow.document.close();
  }

  private injectBaseHref(html: string): string {
    const base = '<base href="' + environment.BaseUrl + '">';
    return /<head[^>]*>/i.test(html)
      ? html.replace(/<head([^>]*)>/i, '<head$1>' + base)
      : base + html;
  }

  private injectReportBridgeScript(html: string, requestedUrl: string): string {
    const script = '<script>(function(){' +
      'window.__daroReportUrl=' + JSON.stringify(requestedUrl) + ';' +
      'var originalOpen=window.open;' +
      'window.open=function(url,target){' +
      'if(window.opener&&url){window.opener.postMessage({type:"daro-auth-report-nav",url:url},"' + window.location.origin + '");return window;}' +
      'return originalOpen ? originalOpen.apply(window, arguments) : null;' +
      '};' +
      '})();</script>';

    // Report pages can execute window.open(..., '_self') while their body is
    // still being parsed. Install the bridge before any report-owned script;
    // appending it at </body> is too late for those immediate navigations.
    return /<head[^>]*>/i.test(html)
      ? html.replace(/<head([^>]*)>/i, '<head$1>' + script)
      : script + html;
  }
}
